import { NavLink, Outlet } from "react-router-dom";
import { Users, Shield, ToggleLeft, LogOut } from "lucide-react";

import { useAuthContext } from "../../auth/AuthContext";
import { isEmbeddedInExtension } from "../../bridge/types";
import { useDarkMode } from "../../theme/useDarkMode";

const NAV_ITEMS = [
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/personas", label: "Personas", icon: Shield },
  { to: "/admin/feature-flags", label: "Feature Flags", icon: ToggleLeft },
];

// Computed once at module load — same signal AppLayout uses. In the ~380px
// side panel the wide left sidebar + p-6 content is too cramped, so embedded
// mode collapses the nav into a horizontal top strip and tightens padding.
const EMBEDDED = isEmbeddedInExtension();

export default function AdminLayout() {
  const { logout } = useAuthContext();
  const { isDark, toggleTheme } = useDarkMode();

  return (
    <div className="min-h-full flex flex-col font-sans text-foreground bg-background">
      {/* Top header */}
      <header
        className={
          "flex items-center justify-between gap-4 py-2.5 bg-card border-b border-border shadow-sm shrink-0 " +
          (EMBEDDED ? "px-3" : "px-6")
        }
      >
        <div className="flex items-center gap-2">
          <div
            style={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 9999,
              background: isDark
                ? "linear-gradient(to bottom, rgba(198, 107, 162, 0.25), transparent)"
                : "linear-gradient(to bottom, rgba(108, 12, 70, 0.12), transparent)",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 2L2 19h20L12 2z"
                fill={isDark ? "hsl(340,45%,60%)" : "hsl(324,80%,24%)"}
                opacity="0.85"
              />
            </svg>
          </div>
          <h1 className="m-0 text-lg font-bold bg-gradient-to-r from-primary to-[hsl(324,60%,45%)] dark:from-[hsl(324,60%,70%)] dark:to-[hsl(324,80%,82%)] bg-clip-text text-transparent">
            Atlas Copilot Admin
          </h1>
          <NavLink
            to="/members"
            className="ml-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to app
          </NavLink>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            {isDark ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      <div className={EMBEDDED ? "flex flex-col flex-1 min-h-0" : "flex flex-1 min-h-0"}>
        {/* Nav — vertical sidebar on web; horizontal scrollable strip in the
            narrow side panel (the w-52 rail won't fit ~380px). */}
        <nav
          className={
            EMBEDDED
              ? "shrink-0 border-b border-border bg-card flex flex-row items-center gap-1 px-2 py-2 overflow-x-auto"
              : "w-52 shrink-0 border-r border-border bg-card flex flex-col py-4 gap-1 px-2"
          }
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors " +
                (EMBEDDED ? "shrink-0 " : "") +
                (isActive
                  ? "bg-primary/10 text-primary"
                  : "text-foreground hover:bg-accent hover:text-accent-foreground")
              }
            >
              <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className={EMBEDDED ? "" : "flex-1"}>{item.label}</span>
            </NavLink>
          ))}
          {!EMBEDDED && (
            <div className="mt-auto pt-4 border-t border-border mx-1">
              <button
                type="button"
                onClick={() => void logout()}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <LogOut className="w-4 h-4" aria-hidden="true" />
                Sign out
              </button>
            </div>
          )}
        </nav>

        {/* Page content */}
        <main className={"flex-1 min-h-0 overflow-auto " + (EMBEDDED ? "p-3" : "p-6")}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
